import { useCallback, useEffect, useRef, type RefObject } from 'react';

const waitForAnimationsToFinish = (
  animatedElement: HTMLElement,
  onComplete: () => void
) => {
  let isCleanedUp = false;
  let animationFrameId = 0;

  const finish = () => {
    if (isCleanedUp) return;

    isCleanedUp = true;
    onComplete();
  };

  animationFrameId = window.requestAnimationFrame(() => {
    if (isCleanedUp) return;

    // In some cases where getAnimations is not available (old browser, or JSDOM), we gracefully degrade to just calling the callback.
    if (!animatedElement.getAnimations) {
      return finish();
    }

    const runningAnimations = animatedElement
      .getAnimations()
      .filter(
        (animation) =>
          animation.playState !== 'finished' && animation.playState !== 'idle'
      );

    if (runningAnimations.length === 0) {
      return finish();
    }

    void Promise.allSettled(
      runningAnimations.map((animation) => animation.finished)
    ).then(finish);
  });

  return () => {
    isCleanedUp = true;
    window.cancelAnimationFrame(animationFrameId);
  };
};

/**
 * Reference: https://github.com/mui/base-ui/blob/master/packages/react/src/internals/useAnimationsFinished.ts
 * We don't need this if we use baseui with `onOpenChangeComplete` prop.
 */
const useAnimationsFinished = (
  animatedElementRef: RefObject<HTMLElement | null>,
  onOpenChangeComplete?: (open: boolean) => void
) => {
  const cancelCurrentWaitRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      cancelCurrentWaitRef.current?.();
    };
  }, []);

  return useCallback(
    (targetOpen: boolean) => {
      const animatedElement = animatedElementRef.current;

      cancelCurrentWaitRef.current?.();
      cancelCurrentWaitRef.current = null;

      if (!animatedElement || !onOpenChangeComplete) {
        onOpenChangeComplete?.(targetOpen);
        return;
      }

      cancelCurrentWaitRef.current = waitForAnimationsToFinish(animatedElement, () => {
        cancelCurrentWaitRef.current = null;
        onOpenChangeComplete(targetOpen);
      });
    },
    [animatedElementRef, onOpenChangeComplete]
  );
};

type UseOpenChangeCompleteParams = {
  open?: boolean;
  ref: RefObject<HTMLElement | null>;
  onOpenChangeComplete?: (open: boolean) => void;
};

/**
 * Smaller implementation based on baseui's useOpenChangeComplete
 * to shim on drawer and dialog components.
 * Reference: https://github.com/mui/base-ui/blob/master/packages/react/src/internals/useOpenChangeComplete.tsx
 */
export const useOpenChangeComplete = ({
  open,
  ref,
  onOpenChangeComplete,
}: UseOpenChangeCompleteParams) => {
  const hasMountedRef = useRef(false);
  const lastObservedOpenRef = useRef<boolean | undefined>(open);
  const queueAnimationsFinished = useAnimationsFinished(ref, onOpenChangeComplete);
  const isUncontrolled = open === undefined;

  useEffect(() => {
    if (isUncontrolled) {
      // useOpenChangeComplete is not supported for uncontrolled usage. Control the open state using the `open` prop.
      return;
    }

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      lastObservedOpenRef.current = open;
      return;
    }

    if (lastObservedOpenRef.current === open) return;

    lastObservedOpenRef.current = open;
    queueAnimationsFinished(open);
  }, [isUncontrolled, open, queueAnimationsFinished, ref]);
};
